import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Tabs, TabList, Tab, TabPanels, TabPanel } from '@carbon/react';
import {
  Report,
  Analytics,
  ChartLine,
  DocumentExport,
  Calendar,
  Location,
  Car,
  Building,
  CheckmarkFilled,
  ArrowRight,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';

const ReportsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const reportTypes = [
    {
      icon: Car,
      title: 'Vehicle Monitoring',
      description: 'Track temperature conditions during transportation',
      image: '/assets/ZoneReportsCar.webp',
      features: ['Route tracking', 'Temperature logs', 'Driver alerts', 'Compliance reports'],
    },
    {
      icon: Building,
      title: 'Office Environment',
      description: 'Monitor workplace environmental standards',
      image: '/assets/ZOneOffice-Reports.webp',
      features: ['Zone monitoring', 'HVAC optimization', 'Employee comfort', 'Energy savings'],
    },
    {
      icon: ChartLine,
      title: 'Data Analytics',
      description: 'Visualize trends with interactive charts',
      image: '/assets/Zone-Office-ReportChart.webp',
      features: ['Trend analysis', 'Pattern detection', 'Forecasting', 'Custom dashboards'],
    },
  ];

  const exportFormats = [
    { name: 'PDF', desc: 'Professional reports for sharing' },
    { name: 'CSV', desc: 'Raw data for analysis' },
    { name: 'Excel', desc: 'Spreadsheet format' },
    { name: 'API', desc: 'Programmatic access' },
  ];

  const complianceFeatures = [
    'HACCP compliance reports',
    'FDA 21 CFR Part 11 support',
    'EN12830 certified logging',
    'Audit trail documentation',
    'Digital signatures',
    'Tamper-proof records',
  ];

  return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="reports-page">
      <style>{`
        button[data-testid="reports-features-cta"]:hover,
        button[data-testid="reports-features-cta"]:active,
        button[data-testid="reports-features-cta"]:focus {
          background-color: ${isDark ? '#393939' : '#d0e2ff'} !important;
          color: ${isDark ? '#78a9ff' : '#0043ce'} !important;
        }
      `}</style>
      {/* Hero */}
      <section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }}>
        <Grid>
          <Column lg={10} md={6} sm={4}>
            <div style={{ color: '#ffffff' }}>
              <Tag type="blue" style={{ marginBottom: '16px' }}>Reporting</Tag>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '24px' }}>
                Comprehensive Reporting
              </h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: '32px' }}>
                Generate detailed reports for compliance, analysis, and decision-making
              </p>
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Report size={20} /> Automated reports
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DocumentExport size={20} /> Multiple formats
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={20} /> Scheduled delivery
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Report Types */}
      <section style={{ backgroundColor: bgSecondary, padding: '80px 0' }} data-testid="report-types">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 style={{ color: textPrimary, textAlign: 'center', marginBottom: '48px', fontSize: '2rem', fontWeight: 600 }}>
              Report Types
            </h2>
          </Column>
          {reportTypes.map((report, index) => (
            <Column lg={5} md={4} sm={4} key={index}>
              <Tile
                className="feature-card"
                style={{
                  backgroundColor: bgPrimary,
                  border: `1px solid ${borderColor}`,
                  padding: '32px',
                  marginBottom: '24px',
                  height: '100%',
                }}
                data-testid={`report-type-${index}`}
              >
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <report.icon size={28} style={{ color: '#ffffff' }} />
                </div>
                <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600 }}>
                  {report.title}
                </h3>
                <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                  {report.description}
                </p>
                <img
                  src={report.image}
                  alt={report.title}
                  style={{ width: '100%', borderRadius: '8px', marginBottom: '16px', border: `1px solid ${borderColor}` }}
                />
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {report.features.map((feature, idx) => (
                    <li key={idx} style={{ color: textSecondary, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <CheckmarkFilled size={14} style={{ color: '#24a148' }} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </Tile>
            </Column>
          ))}
        </Grid>
      </section>

      {/* Export Formats */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }} data-testid="export-formats">
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <Tag type="purple">Export Options</Tag>
            <h2 style={{ color: textPrimary, marginTop: '16px', marginBottom: '24px', fontSize: '2.5rem', fontWeight: 600 }}>
              Multiple Export Formats
            </h2>
            <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '1rem', lineHeight: 1.8 }}>
              Export your data in the format that works best for your needs. From professional PDF reports for stakeholders to raw CSV data for analysis.
            </p>
            <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {exportFormats.map((format, idx) => (
                <Tile
                  key={idx}
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    padding: '20px',
                  }}
                  data-testid={`export-format-${format.name.toLowerCase()}`}
                >
                  <DocumentExport size={24} style={{ color: '#0f62fe', marginBottom: '8px' }} />
                  <h4 style={{ color: textPrimary, marginBottom: '4px', fontWeight: 600 }}>{format.name}</h4>
                  <p style={{ color: textSecondary, fontSize: '0.75rem' }}>{format.desc}</p>
                </Tile>
              ))}
            </div>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '32px',
                height: '100%',
              }}
            >
              <Analytics size={48} style={{ color: '#6929c4', marginBottom: '24px' }} />
              <h3 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.5rem', fontWeight: 600 }}>
                Scheduled Reports
              </h3>
              <p style={{ color: textSecondary, marginBottom: '24px' }}>
                Set up automated report generation and delivery. Get daily, weekly, or monthly reports sent directly to your inbox.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Daily summaries', 'Weekly digests', 'Monthly compliance reports', 'Custom schedules'].map((item, idx) => (
                  <li key={idx} style={{ color: textSecondary, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <CheckmarkFilled size={16} style={{ color: '#24a148' }} /> {item}
                  </li>
                ))}
              </ul>
            </Tile>
          </Column>
        </Grid>
      </section>

      {/* Compliance */}
      <section style={{ backgroundColor: bgSecondary, padding: '80px 0' }} data-testid="compliance-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="teal">Compliance</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                Compliance Ready
              </h2>
              <p style={{ color: textSecondary, maxWidth: '600px', margin: '16px auto 0' }}>
                Meet industry standards with our comprehensive compliance reporting features
              </p>
            </div>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <Tile
              style={{
                backgroundColor: bgPrimary,
                border: `1px solid ${borderColor}`,
                padding: '32px',
              }}
            >
              <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.25rem', fontWeight: 600 }}>
                Compliance Features
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {complianceFeatures.map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
                    <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </Tile>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <Tile
              style={{
                backgroundColor: '#0f62fe',
                padding: '32px',
                color: '#ffffff',
              }}
              data-testid="compliance-cta"
            >
              <h3 style={{ marginBottom: '16px', fontSize: '1.25rem', fontWeight: 600 }}>
                Need Custom Compliance Reports?
              </h3>
              <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Our team can help you create custom reports tailored to your specific regulatory requirements.
              </p>
              <Button kind="secondary" onClick={() => navigate('/contact')} data-testid="compliance-contact">
                Talk to an Expert <ArrowRight style={{ marginLeft: '8px' }} />
              </Button>
            </Tile>
          </Column>
        </Grid>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '16px' }}>
                Start Generating Reports Today
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px' }}>
                Sign up and get instant access to all reporting features
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button kind="primary" size="lg" onClick={() => navigate('/signup')} data-testid="reports-signup-cta">
                  Get Started Free
                </Button>
                <Button
                  kind="tertiary"
                  size="lg"
                  onClick={() => navigate('/features')}
                  data-testid="reports-features-cta"
                  style={{ backgroundColor: 'transparent' }}
                >
                  View All Features
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default ReportsPage;
